import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CalldiusStepItem, CallidusStepperComponent } from '@shared/components/callidus-stepper/callidus-stepper.component';
import { BoatDataHaftpflichtComponent } from '../../components/boat-data-haftpflicht/boat-data-haftpflicht.component';
import { CustomerDataComponent } from '@shared/components/customer-data/customer-data.component';
import {
  AdditionalCoverageHaftpflichtDataComponent
} from '../../components/additional-coverage-haftpflicht-data/additional-coverage-haftpflicht-data.component';
import { takeUntil } from 'rxjs';
import { DataSubmittedComponent } from '@shared/components/data-submitted/data-submitted.component';
import { BvsBaseComponent } from '@shared/directives/bvs-base.component';
import { BvsHaftpflichtFormModel } from '../../models/form/bvs-haftpflicht-form.model';
import { createBvsHaftpflichtForm } from '../../factories/bvs-haftpflicht-form.factory';
import { Router } from '@angular/router';
import {
  BrokernetLanguageType,
  CallidusBvsHaftpflichtBoatInformation,
  CallidusBvsHaftpflichtFigures,
  PublicAddressCreateDto,
  PublicCallidusBvsHaftpflichtContractCreateDto,
  PublicPersonCreateDto
} from '@backend/models';
import { BvsHaftpflichtStateService } from '@shared/services/bvs-haftpflicht-state.service';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';

@Component({
  selector: 'app-bvs-haftpflicht-page',
  imports: [
    CallidusStepperComponent,
    BoatDataHaftpflichtComponent,
    CustomerDataComponent,
    AdditionalCoverageHaftpflichtDataComponent,
    DataSubmittedComponent
  ],
  providers: [BvsHaftpflichtStateService],
  templateUrl: './bvs-haftpflicht-page.component.html',
  styleUrl: './bvs-haftpflicht-page.component.scss'
})
export class BvsHaftpflichtPageComponent extends BvsBaseComponent implements OnInit {
  private bvsHaftpflichtStateService = inject(BvsHaftpflichtStateService);
  private readonly router = inject(Router);
  private readonly urlParamProviderService = inject(UrlParamProviderService);

  form!: FormGroup<BvsHaftpflichtFormModel>;
  curentNavigationStep = HaftpflichtNavigationEnum.Base;

  stepItems: CalldiusStepItem[] = [
    { id: '0', label: 'bvs.calculator.menu.boat', iconUrl: '/assets/icons/boat.svg' },
    { id: '1', label: 'bvs.calculator.menu.finish', iconUrl: '/assets/icons/health-shield.svg' }
  ];
  protected readonly HaftpflichtNavigationEnum = HaftpflichtNavigationEnum;
  protected readonly FormGroup = FormGroup;

  private stepOrder: HaftpflichtNavigationEnum[] = [
    HaftpflichtNavigationEnum.Base,
    HaftpflichtNavigationEnum.AdditionalCoverage,
    HaftpflichtNavigationEnum.Customer,
    HaftpflichtNavigationEnum.Done
  ];

  getCurrentPosition(): number {
    return this.stepOrder.indexOf(this.curentNavigationStep);
  }

  moveToNextStep(): void {
    this.curentNavigationStep++;
  }

  moveToPreviousStep(): void {
    if (this.curentNavigationStep === HaftpflichtNavigationEnum.Base) {
      void this.router.navigate(['/']);
    }
    this.curentNavigationStep--;
  }

  ngOnInit(): void {
    this.form = createBvsHaftpflichtForm();

    this.form?.controls.bvsBoatInformation.controls.boatType.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(boatType => {
      if (boatType !== null) {
        this.setValidatorsBasedOnBoatType(this.form, boatType);
      }
    });
  }

  submitData(): void {
    const person = this.form?.controls.customerData.controls.person.getRawValue();
    const legalChecks = this.form.controls.customerData.controls.legalChecks.controls;
    const boatValues = this.form.controls.bvsBoatInformation.getRawValue();
    const figureValues = this.form.controls.figures.getRawValue();

    const query = this.urlParamProviderService.getQueryData();
    const isLinked = !!query?.customerId && !!query?.personId;

    const onboardingDto: PublicCallidusBvsHaftpflichtContractCreateDto = {
      publicLegalCheck: {
        hatElektronischeKommunikationAkzeptiert: legalChecks.isContactConsent.value ?? false,
        hatDsgvoAkzeptiert: legalChecks.isGdprAccepted.value ?? false,
        hatAvbAkzeptiert: false,
        hatIpidAkzeptiert: false,
        hatSepaEinzugAkzeptiert: false,
        hatWiderrufsrechtAkzeptiert: false,
        hatWuenscheUndBeduerfnisseAkzeptiert: false
      },

      ...(isLinked
        ? { publicLinkCustomerDto: { customerId: query!.customerId!, personId: query!.personId! } }
        : {
          publicPerson: {
            titlePrefix: person.titlePrefix,
            titleSuffix: person.titleSuffix,
            firstname: person.firstname,
            lastname: person.lastname,
            birthdate: person.birthdate,
            gender: person.gender,
            brokernetLanguageType: localStorage.getItem('app-language') === BrokernetLanguageType.En ? BrokernetLanguageType.En : BrokernetLanguageType.De,
            publicContactInfoCreateDto: {
              email: person.email,
              phone: person.phone
            },
            publicAddressCreateDto: this.form.controls.customerData.controls.address.getRawValue() as PublicAddressCreateDto
          } as PublicPersonCreateDto
        }),

      callidusBvsHaftpflichtBoatInformation: {
        boatType: boatValues.boatType,
        boatConstructionType: boatValues.boatConstructionType ?? undefined,
        sailingArea: boatValues.sailingArea,
        countryRegistered: boatValues.countryRegistered,
        mastMaterial: boatValues.mastMaterial ?? undefined,
        isEnginePresent: boatValues.isEnginePresent ?? false,
        isGutachtenPresent: boatValues.isGutachtenPresent ?? false,
        isComfortAusstattungVorhanden: boatValues.isComfortAusstattungVorhanden ?? false,
        isSailPresent: boatValues.isSailPresent ?? false,
        maxKnotsOver40: boatValues.maxKnotsOver40 ?? false,
        isBeibootVorhanden: boatValues.isBeibootVorhanden ?? false,
        manufacturer: boatValues.manufacturer,
        yachtType: boatValues.yachtType,
        boatModel: boatValues.boatModel,
        boatYard: boatValues.boatYard,
        engineManufacturer: boatValues.engineManufacturer,
        engineModel: boatValues.engineModel,
        engineFuelType: boatValues.engineFuelType,
        mooringSummer: boatValues.mooringSummer,
        mooringWinter: boatValues.mooringWinter,
        fullWidth: boatValues.fullWidth,
        fullLength: boatValues.fullLength,
        engineHp: boatValues.engineHp,
        builtInEngineCount: boatValues.builtInEngineCount,
        builtInEngineHp: boatValues.builtInEngineHp,
        extraEngineCount: boatValues.extraEngineCount,
        extraEngineHp: boatValues.extraEngineHp,
        sailSurfaceInMetersSquared: boatValues.sailSurfaceInMetersSquared,
        beibootHp: boatValues.beibootHp
      } as CallidusBvsHaftpflichtBoatInformation,

      callidusBvsHaftpflichtFigures: {
        isRechtsschutzSelected: figureValues.isRechtsschutzSelected ?? false,
        isKeinSelbstbehaltBeiRechtsschutzSelected: figureValues.isKeinSelbstbehaltBeiRechtsschutzSelected ?? false,
        isCharterSelected: figureValues.isCharterSelected ?? false,
        callidusBvsVersicherungssummeKunde: figureValues.callidusBvsVersicherungssummeKunde
      } as CallidusBvsHaftpflichtFigures,

      anmerkungCustomer: this.form.controls.customerData.controls.anmerkungCustomer.value!
    };

    this.bvsHaftpflichtStateService
      .submitHaftpflichtOnboarding(onboardingDto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.curentNavigationStep = HaftpflichtNavigationEnum.Done;
        },
        error: (error) => {
          console.error('Error submitting onboarding:', error);
        }
      });
  }
}

export enum HaftpflichtNavigationEnum {
  Base,
  AdditionalCoverage,
  Customer,
  Done
}
